import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageInfoService, PageLink } from '@metronic/layout';
import { TableColumn } from '@shared/interface/table.interface';
import { UtilitiesService } from '@services/utilities.service';
import { BaseDatasource } from '@shared/base/base.datasource';
import { IQrBatchItem } from '../qr-products.interface';
import { BaseTableService } from '@shared/base/base-table-service.service';
import { API } from '@config/constants/api.constant';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IQueryParams } from '@shared/interface/urlparam.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { UrlUtilsService } from '@utils/url-utils.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { ModalConfirmDownloadComponent } from '../components/modal-confirm-download/modal-confirm-download.component';
import { QrProductsService } from '../qr-products.service';
import { MatSort, Sort } from '@angular/material/sort';
import { FilterService } from '@services/filter.service';

@Component({
  selector: 'app-qr-products-list',
  templateUrl: './qr-products-list.component.html',
  styleUrls: ['./qr-products-list.component.scss'],
})
export class QrProductsListComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: false }) matSort: MatSort;
  baseDatasource: BaseDatasource<IQrBatchItem>;

  displayedColumns: string[];
  tableColumns: TableColumn[] = [
    {
      key: 'batch_name',
      title: 'Nama Batch',
      isSortable: false,
    },
    {
      key: 'count_all_qr',
      title: 'Jumlah QR',
      isSortable: false,
    },
    {
      key: 'create_by',
      title: 'Dibuat Oleh',
      isSortable: false,
    },
    {
      key: 'createdDate',
      title: 'Tanggal Pembuatan',
      isSortable: true,
    },
    {
      key: 'request_generate_qr_status_name',
      title: 'Status',
      isSortable: false,
    },
    {
      key: 'update_by',
      title: 'diunduh oleh',
      isSortable: false,
    },
    {
      key: 'modifiedDate',
      title: 'tanggal unduh',
      isSortable: true,
    },
    {
      key: 'actions',
      title: 'Actions',
      isSortable: false,
    },
  ];

  links: Array<PageLink> = [
    {
      title: 'Product Catalog',
      path: 'products/list-published',
      isActive: false,
    },
    {
      title: '',
      path: '',
      isActive: false,
      isSeparator: true,
    },
    {
      title: 'Qr Produk',
      path: '',
      isActive: true,
    },
  ];

  search: string;
  isActiveFilter = false;

  selectedQrBatchToDownload = new BehaviorSubject<IQrBatchItem | null>(null);
  @ViewChild('modalConfirmDownload') modalConfirmDownload: ModalConfirmDownloadComponent;

  private unsubscribe: Subscription[] = [];

  get PrivilegeAddBatch() {
    return this.qrProductService.hasListPrivilegeCTA('CTA_ADD_BATCH');
  }

  get PrivilegeDownloadBatch() {
    return this.qrProductService.hasListPrivilegeCTA('CTA_DOWNLOAD');
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private paginationService: UrlUtilsService,
    private pageInfoService: PageInfoService,
    private baseTableService: BaseTableService<IQrBatchItem>,
    private qrProductService: QrProductsService,
    public utilitiesService: UtilitiesService,
    private filterService: FilterService
  ) {}

  ngOnInit() {
    this.initPageInfo();
    this.setTableData();
    this.queryHandler();
  }

  initPageInfo() {
    this.pageInfoService.updateTitle('Qr Produk', undefined, true);
    this.pageInfoService.updateBreadcrumbs(this.links);
  }

  setTableData() {
    const _responseSubs = this.baseTableService.responseDatabase$.subscribe((resp) => (this.baseDatasource = resp));

    this.baseDatasource.hasItems = false;

    this.displayedColumns = this.tableColumns.map((item) => item.key);
    this.unsubscribe.push(_responseSubs);
  }

  renderThClass(_columnKey: string) {
    return _columnKey === 'actions'
      ? 'min-w-70px text-center'
      : _columnKey === 'batch_name'
      ? 'mw-lg-300px'
      : _columnKey === 'create_date'
      ? 'mw-125px'
      : _columnKey === 'update_by' || _columnKey === 'update_date'
      ? 'mw-100px'
      : _columnKey === 'create_by' || _columnKey === 'request_generate_qr_status_name'
      ? 'min-w-200px'
      : 'min-w-150px';
  }

  changePageEvent(e: BaseDatasource<IQrBatchItem>) {
    const { pageSize, current_page } = e;
    const dataParams: IQueryParams = {
      page: current_page,
      size: pageSize,
      search: '' || this.search,
    };

    return this.router.navigate([], {
      queryParams: {
        ...dataParams,
      },
      queryParamsHandling: 'merge',
    });
  }

  handleActionDetail(_id: string) {
    return this.router.navigate([`/products/qr-produk/${_id}`]);
  }

  handleActionAddBatch() {
    return this.router.navigate(['/products/generate-qr-batch']);
  }

  handleActionDownload(_item: IQrBatchItem) {
    this.selectedQrBatchToDownload.next(_item);
    return this.modalConfirmDownload.openConfirmDialog();
  }

  queryHandler() {
    const _paramSubs = this.activatedRoute.queryParams.subscribe((_params) => {
      const { search, status } = _params;

      this.search = search;
      this.isActiveFilter = !!status;

      const _urlParam = this.paginationService.sliceQueryParams();
      this.baseTableService.loadDataTable(API.LIST_QR_BATCH, _urlParam ? _urlParam : '');
    });

    this.unsubscribe.push(_paramSubs);
  }

  isAbleToDownloadBatch = (status: string) => this.qrProductService.isAbleToDownloadBatch(status);

  isEmptyData = () => !this.search && !this.isActiveFilter && this.baseDatasource.isTableLoaded && !this.baseDatasource.hasItems;

  searchNotFound = () => this.baseDatasource.isTableLoaded && this.baseDatasource.totalItem$.value === 0 && !!(this.search || this.isActiveFilter);

  sortTable = (param: Sort) => this.filterService.sortTable(param, this.tableColumns);

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
